import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TVFilterPanel from '../components/TVFilterPanel';
import TVProductCard from '../components/TVProductCard';
import { televisions } from '../data/televisions';

export default function Televisions() {
    const navigate = useNavigate();
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [sortBy, setSortBy] = useState('best-selling');
    const [selectedFilters, setSelectedFilters] = useState({
        brand: [], priceRange: [0, 400000], screenSize: [],
        displayType: [], resolution: [], smartTV: [], os: [],
        refreshRate: [], hdmiPorts: [], availability: [], discount: [], rating: []
    });

    const filterOptions = useMemo(() => {
        const brands = [...new Set(televisions.map(tv => tv.brand))].sort();
        const screenSizes = [...new Set(televisions.map(tv => tv.screenSize))].sort((a, b) => parseInt(a) - parseInt(b));
        const displayTypes = [...new Set(televisions.map(tv => tv.displayType))].sort();
        const resolutions = ['HD', 'Full HD', '4K', '8K'];
        const smartTV = ['Yes', 'No'];
        const operatingSystems = [...new Set(televisions.map(tv => tv.os))].sort();
        const refreshRates = [...new Set(televisions.map(tv => tv.refreshRate))].sort();
        const hdmiPorts = [...new Set(televisions.map(tv => tv.hdmiPorts))].sort((a, b) => parseInt(a) - parseInt(b));
        const prices = televisions.map(tv => tv.price);
        return { brands, priceRange: [Math.min(...prices), Math.max(...prices)], screenSizes, displayTypes, resolutions, smartTV, operatingSystems, refreshRates, hdmiPorts };
    }, []);

    const filteredProducts = useMemo(() => {
        let filtered = televisions.filter(tv => {
            if (selectedFilters.brand.length > 0 && !selectedFilters.brand.includes(tv.brand)) return false;
            if (tv.price > selectedFilters.priceRange[1]) return false;
            if (selectedFilters.screenSize.length > 0 && !selectedFilters.screenSize.includes(tv.screenSize)) return false;
            if (selectedFilters.displayType.length > 0 && !selectedFilters.displayType.includes(tv.displayType)) return false;
            if (selectedFilters.resolution.length > 0 && !selectedFilters.resolution.includes(tv.resolution)) return false;
            if (selectedFilters.smartTV.length > 0 && !selectedFilters.smartTV.includes(tv.smartTV)) return false;
            if (selectedFilters.os.length > 0 && !selectedFilters.os.includes(tv.os)) return false;
            if (selectedFilters.refreshRate.length > 0 && !selectedFilters.refreshRate.includes(tv.refreshRate)) return false;
            if (selectedFilters.hdmiPorts.length > 0 && !selectedFilters.hdmiPorts.includes(tv.hdmiPorts)) return false;
            return true;
        });
        switch (sortBy) {
            case 'price-low': filtered.sort((a, b) => a.price - b.price); break;
            case 'price-high': filtered.sort((a, b) => b.price - a.price); break;
            case 'newest': filtered.sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()); break;
            case 'best-selling': filtered.sort((a, b) => b.sales - a.sales); break;
        }
        return filtered;
    }, [selectedFilters, sortBy]);

    const handleClearFilters = () => setSelectedFilters({ brand: [], priceRange: filterOptions.priceRange, screenSize: [], displayType: [], resolution: [], smartTV: [], os: [], refreshRate: [], hdmiPorts: [], availability: [], discount: [], rating: [] });

    return (
        <div className="min-h-screen bg-muted/50 pt-[10px]">
            <div className="bg-card border-b border-border">
                <div className="max-w-[1400px] mx-auto px-4 py-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold mb-2">Televisions</h1>
                            <p className="text-muted-foreground">Showing {filteredProducts.length} of {televisions.length} products</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button onClick={() => setFilterPanelOpen(true)} className="lg:hidden flex items-center gap-2 px-4 py-2.5 border border-border rounded-lg hover:bg-muted/50 whitespace-nowrap">
                                <i className="ri-filter-3-line"></i> Filters
                            </button>
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-foreground/80 whitespace-nowrap">Sort by:</label>
                                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm cursor-pointer">
                                    <option value="best-selling">Best Selling</option>
                                    <option value="price-low">Price: Low to High</option>
                                    <option value="price-high">Price: High to Low</option>
                                    <option value="newest">Newest</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 py-8 flex gap-6">
                <div className="hidden lg:block w-64 flex-shrink-0">
                    <TVFilterPanel filters={filterOptions} selectedFilters={selectedFilters} onFilterChange={(type, val) => setSelectedFilters(prev => ({ ...prev, [type]: val }))} onClearFilters={handleClearFilters} productCount={filteredProducts.length} isOpen={false} onClose={() => { }} />
                </div>
                <div className="flex-1">
                    {filteredProducts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredProducts.map(product => <TVProductCard key={product.id} product={product} />)}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <h3 className="text-xl font-semibold mb-2">No televisions found</h3>
                            <p className="text-muted-foreground mb-6">Try adjusting your filters</p>
                            <button onClick={handleClearFilters} className="px-6 py-3 bg-[#FF6B35] text-white rounded-lg font-semibold whitespace-nowrap">Clear All Filters</button>
                        </div>
                    )}
                </div>
            </div>
            {filterPanelOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setFilterPanelOpen(false)}></div>
                    <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-card shadow-xl overflow-y-auto">
                        <div className="sticky top-0 bg-card border-b border-border px-4 py-4 flex items-center justify-between z-10">
                            <h2 className="text-lg font-bold text-foreground">Filters</h2>
                            <button onClick={() => setFilterPanelOpen(false)} className="w-8 h-8 flex items-center justify-center text-muted-foreground"><i className="ri-close-line text-xl"></i></button>
                        </div>
                        <div className="p-4">
                            <TVFilterPanel filters={filterOptions} selectedFilters={selectedFilters} onFilterChange={(type, val) => setSelectedFilters(prev => ({ ...prev, [type]: val }))} onClearFilters={handleClearFilters} productCount={filteredProducts.length} isOpen={filterPanelOpen} onClose={() => setFilterPanelOpen(false)} />
                        </div>
                        <div className="sticky bottom-0 bg-card border-t border-border p-4">
                            <button onClick={() => setFilterPanelOpen(false)} className="w-full py-3 bg-[#FF6B35] text-white rounded-lg font-medium whitespace-nowrap">Apply Filters</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
